// ═══════════════════════════════════════════════════════════════
// PixAlloy — Alloyhead Schema Definition
// ═══════════════════════════════════════════════════════════════
// This schema defines the ABI contract between the PixAlloy React UI
// (Tier 1) and the pixel engine (Tier 3). The Alloyhead framework
// compiles this into SharedArrayBuffer layouts with atomic access.
//
// Import and pass to: new HostBridge(PIXALLOY_SCHEMA, config, callbacks)
// ═══════════════════════════════════════════════════════════════

import type { Schema } from '@alloyhead/core';

export const PIXALLOY_SCHEMA: Schema = {
  version: 1,
  schema_id: 'pixalloy-v0.8',

  parameters: {
    // ── Cursor (written every mousemove — hot path) ──
    cursorX:         { type: 'Float32', default: -99 },
    cursorY:         { type: 'Float32', default: -99 },
    cursorDown:      { type: 'Uint32',  default: 0 },
    pressure:        { type: 'Float32', default: 1.0 },

    // ── Brush settings ──
    brushSize:       { type: 'Float32', default: 12 },
    brushOpacity:    { type: 'Float32', default: 1.0 },
    brushHardness:   { type: 'Float32', default: 0.8 },
    brushR:          { type: 'Uint32',  default: 132 },
    brushG:          { type: 'Uint32',  default: 17 },
    brushB:          { type: 'Uint32',  default: 22 },
    brushTip:        { type: 'Uint32',  default: 0, enum: ['round','square','scatter','texture','flat','hair','grass','splatter','spray'] },
    pressureSize:    { type: 'Uint32',  default: 1 },
    pressureOpacity: { type: 'Uint32',  default: 0 },

    // ── Brush dynamics (Phase 3) ──
    brushSpacing:    { type: 'Float32', default: 10 },   // % of diameter (1-100)
    brushFlow:       { type: 'Float32', default: 1.0 },  // per-stamp alpha (0-1)
    sizeJitter:      { type: 'Float32', default: 0 },    // random size % (0-1)
    angleJitter:     { type: 'Float32', default: 0 },    // random rotation (0-1 = 0-360°)
    opacityJitter:   { type: 'Float32', default: 0 },    // random opacity % (0-1)
    scatterAmount:   { type: 'Float32', default: 0 },    // perpendicular offset % (0-1)
    brushSmoothing:  { type: 'Float32', default: 0 },    // input averaging (0-1)
    pressureHardness:{ type: 'Uint32',  default: 0 },    // pressure → hardness toggle
    pressureFlow:    { type: 'Uint32',  default: 0 },    // pressure → flow toggle
    pressureCurve:   { type: 'Float32', default: 1.0 },  // power curve (0.5=light, 1=linear, 2=heavy)
    tiltX:           { type: 'Float32', default: 0 },    // pen tilt X (-90 to 90)
    tiltY:           { type: 'Float32', default: 0 },    // pen tilt Y (-90 to 90)

    // ── Tool + layer selection ──
    activeTool:      { type: 'Uint32',  default: 0 },
    activeLayer:     { type: 'Uint32',  default: 0 },

    // ── Viewport adjustments (non-destructive CSS filters) ──
    brightness:      { type: 'Float32', default: 0 },
    contrast:        { type: 'Float32', default: 0 },
    saturation:      { type: 'Float32', default: 0 },
  },

  // Cache-line groups: params written together share a 64-byte boundary
  access_groups: [
    ['cursorX', 'cursorY', 'cursorDown', 'pressure'],
    ['brushSize', 'brushOpacity', 'brushHardness', 'brushR', 'brushG', 'brushB'],
    ['activeTool', 'activeLayer'],
  ],

  commands: [
    // ── Core ──
    { id: 1,  name: 'UNDO' },
    { id: 2,  name: 'REDO' },
    { id: 3,  name: 'LAYER_ADD' },
    { id: 4,  name: 'LAYER_DEL' },
    { id: 6,  name: 'FILTER',        payload: { id: 'Uint32' } },
    { id: 7,  name: 'STROKE_ON' },
    { id: 8,  name: 'STROKE_OFF' },
    { id: 9,  name: 'FLATTEN' },
    { id: 11, name: 'LAYER_CLR' },
    { id: 12, name: 'LAYER_UP' },
    { id: 13, name: 'LAYER_DN' },

    // ── Selection ──
    { id: 20, name: 'SEL_RECT',    payload: { x: 'Float32', y: 'Float32', w: 'Float32', h: 'Float32' } },
    { id: 21, name: 'SEL_ELLIPSE', payload: { x: 'Float32', y: 'Float32', w: 'Float32', h: 'Float32' } },
    { id: 25, name: 'SEL_CLR' },
    { id: 26, name: 'SEL_LASSO' },
    { id: 27, name: 'SEL_WAND',    payload: { x: 'Float32', y: 'Float32', tolerance: 'Float32' } },
    { id: 28, name: 'SEL_FEATHER', payload: { radius: 'Float32' } },
    { id: 29, name: 'SEL_POLY' },
    { id: 61, name: 'SEL_ALL' },
    { id: 62, name: 'SEL_INV' },

    // ── Transform ──
    { id: 30, name: 'TFM_BEGIN' },
    { id: 31, name: 'TFM_APPLY' },
    { id: 32, name: 'TFM_CANCEL' },
    { id: 33, name: 'TFM_SEL_BEGIN' },
    { id: 34, name: 'TFM_SEL_APPLY' },
    { id: 35, name: 'TFM_UPDATE' },

    // ── Clipboard ──
    { id: 40, name: 'COPY' },
    { id: 41, name: 'PASTE' },
    { id: 42, name: 'CROP',        payload: { x: 'Float32', y: 'Float32', w: 'Float32', h: 'Float32' } },
    { id: 43, name: 'CUT' },

    // ── Move ──
    { id: 50, name: 'MOVE',        payload: { dx: 'Float32', dy: 'Float32' } },

    // ── Text (sideband — complex payloads) ──
    { id: 60, name: 'TEXT_ADD' },
    { id: 63, name: 'TEXT_UPDATE' },

    // ── Layer effects (sideband — nested objects) ──
    { id: 70, name: 'FX_UPDATE' },

    // ── Fill ──
    { id: 100, name: 'FILL',       payload: { x: 'Float32', y: 'Float32' } },
    { id: 101, name: 'RUBBER_STAMP_SET', payload: { x: 'Float32', y: 'Float32' } },
    { id: 102, name: 'FILL_COLOR', payload: { r: 'Uint32', g: 'Uint32', b: 'Uint32' } },

    // ── Layer properties ──
    { id: 80, name: 'LAYER_OP',    payload: { op: 'Float32' } },
    { id: 81, name: 'LAYER_BLEND', payload: { blend: 'Uint32' } },
    { id: 82, name: 'MERGE_DOWN' },

    // ── Masks ──
    { id: 90, name: 'MASK_ADD' },
    { id: 91, name: 'MASK_DEL' },
    { id: 92, name: 'MASK_TOGGLE' },
    { id: 93, name: 'MASK_LINK' },

    // ── Image operations ──
    { id: 110, name: 'IMG_FLIP_H' },
    { id: 111, name: 'IMG_FLIP_V' },
    { id: 112, name: 'IMG_ROT90' },
    { id: 113, name: 'IMG_ROT180' },
    { id: 114, name: 'IMG_RESIZE',   payload: { w: 'Uint32', h: 'Uint32' } },
    { id: 115, name: 'CANVAS_SIZE',  payload: { w: 'Uint32', h: 'Uint32' } },

    // ── History / Levels ──
    { id: 120, name: 'LEVELS' },
    { id: 121, name: 'LAYER_REORDER', payload: { from: 'Uint32', to: 'Uint32' } },

    // ── Adjustment layers ──
    { id: 130, name: 'ADJ_LAYER_ADD' },
    { id: 131, name: 'ADJ_LAYER_UPDATE' },

    // ── Filters ──
    { id: 140, name: 'HIST_JUMP',     payload: { idx: 'Uint32' } },
    { id: 141, name: 'HUESAT_FILTER', payload: { hue: 'Float32', sat: 'Float32', light: 'Float32', range: 'Uint32' } },
    { id: 142, name: 'GAUSS_BLUR',    payload: { radius: 'Float32' } },
    { id: 143, name: 'COLOR_BALANCE' },
    { id: 144, name: 'SEL_FROM_ALPHA', payload: { idx: 'Uint32', mode: 'Uint32' } },

    // ── Groups ──
    { id: 150, name: 'GROUP_ADD' },
    { id: 151, name: 'GROUP_TOGGLE' },
    { id: 152, name: 'LAYER_RENAME' },
    { id: 153, name: 'CONTENT_FILL' },
    { id: 154, name: 'GROUP_MOVE_IN' },
    { id: 155, name: 'GROUP_MOVE_OUT' },

    // ── Pen tool ──
    { id: 160, name: 'PEN_TO_SEL' },
    { id: 161, name: 'PEN_STROKE' },
    { id: 162, name: 'PEN_FILL' },
    { id: 180, name: 'VECTOR_LAYER_ADD' },
    { id: 181, name: 'VECTOR_LAYER_UPDATE' },

    // ── v0.8 ──
    { id: 170, name: 'ZOOM_FIT' },
    { id: 171, name: 'DUPLICATE_LAYER' },
    { id: 200, name: 'VIS' },
  ],

  ring_buffer: {
    capacity: 256,
    slot_size: 32,
  },
};
